import { useMemo, useState } from "react";

import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Eye,
  Search,
  Table2,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { SchemaNode } from "../api";

/**
 * Left-hand database tree: schemas → tables, with a filter box. A table is
 * selected by clicking it; the active row is highlighted like the app sidebar.
 */

export interface TableRef {
  schema: string;
  table: string;
}

interface Props {
  schemas: SchemaNode[];
  loading: boolean;
  selected: TableRef | null;
  onSelect: (ref: TableRef) => void;
}

function fmtRows(n: number | null): string {
  if (n == null) return "";
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export function SchemaSidebar({ schemas, loading, selected, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return schemas;
    return schemas
      .map((s) => ({
        ...s,
        tables: s.tables.filter(
          (t) =>
            t.name.toLowerCase().includes(q) || s.name.toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.tables.length > 0);
  }, [schemas, q]);

  const totalTables = filtered.reduce((n, s) => n + s.tables.length, 0);

  function toggle(name: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(schemas.map((s) => s.name)));
  const allCollapsed =
    schemas.length > 0 && schemas.every((s) => collapsed.has(s.name));

  return (
    <div className="flex h-full flex-col rounded-lg border bg-card">
      <div className="border-b p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter tables…"
            className="h-9 pl-8"
          />
        </div>
      </div>

      {!q && schemas.length > 0 ? (
        <div className="flex items-center justify-end gap-1 border-b px-2 py-1">
          <button
            type="button"
            onClick={expandAll}
            disabled={collapsed.size === 0}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
          >
            <ChevronsUpDown className="h-3 w-3" />
            Expand all
          </button>
          <button
            type="button"
            onClick={collapseAll}
            disabled={allCollapsed}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
          >
            <ChevronsDownUp className="h-3 w-3" />
            Collapse all
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {loading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-5 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {q ? "No tables match." : "No tables found."}
          </p>
        ) : (
          filtered.map((s) => {
            const isCollapsed = collapsed.has(s.name) && !q;
            return (
              <div key={s.name} className="mb-0.5">
                <button
                  type="button"
                  onClick={() => toggle(s.name)}
                  className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  )}
                  <span className="truncate">{s.name}</span>
                  <span className="ml-auto font-normal tabular-nums">
                    {s.tables.length}
                  </span>
                </button>

                {!isCollapsed &&
                  s.tables.map((t) => {
                    const active =
                      selected?.schema === s.name && selected?.table === t.name;
                    const Icon = t.kind === "table" ? Table2 : Eye;
                    return (
                      <button
                        key={t.name}
                        type="button"
                        onClick={() => onSelect({ schema: s.name, table: t.name })}
                        title={t.comment ?? `${s.name}.${t.name}`}
                        className={cn(
                          "flex w-full items-center gap-2 py-1.5 pl-7 pr-2 text-left text-sm",
                          active
                            ? "bg-power-light font-medium text-power"
                            : "text-foreground/80 hover:bg-accent hover:text-foreground",
                        )}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />
                        <span className="truncate">{t.name}</span>
                        {t.est_rows != null ? (
                          <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">
                            {fmtRows(t.est_rows)}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>

      <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        {loading ? "…" : `${filtered.length} schemas · ${totalTables} tables`}
      </div>
    </div>
  );
}
